'use client';

import React from 'react';
import type {
    DialogProps as AriaDialogProps,
    ModalOverlayProps as AriaModalOverlayProps,
    ModalRenderProps,
} from 'react-aria-components';
import {
    Dialog as AriaDialog,
    DialogTrigger as AriaDialogTrigger,
    Modal as AriaModal,
    ModalOverlay as AriaModalOverlay,
} from 'react-aria-components';
import { cx } from '../../../utils/cx';
import { isReactComponent } from '../../../utils/isReactComponent';
import { CloseButton, CloseButtonProps } from '../button/CloseButton';
import { ModalHeader } from './ModalHeader';
import { getModalScale, getModalShadow } from './modalDepthUtils';
import type { ModalTransitionStyle } from './ModalStackContext';
import { ModalStackLayerProvider, useModalStackLayer, useModalStackPresentation } from './ModalStackContext';

const SEMANTIC_NAME_MODAL_OVERLAY = 'RomaModalOverlay';
const SEMANTIC_NAME_MODAL = 'RomaModal';
const SEMANTIC_NAME_DIALOG = 'RomaDialog';
const SEMANTIC_NAME_DIALOG_CONTENT = 'RomaDialogContent';
const styles = {
    overlay: {
        backgroundStyle: {
            dim: 'bg-bg-overlay-alpha',
            solid: 'bg-bg-overlay-solid',
            none: 'bg-transparent',
        },
    },
    modal: {
        transition: 'duration-200 ease-in-out motion-reduce:animate-none motion-reduce:transition-none',
    },
};

const DialogTrigger = AriaDialogTrigger;

export type ModalBackgroundStyle = 'dim' | 'solid' | 'none';

export type ModalOverlayProps = {
    backgroundStyle?: ModalBackgroundStyle;
    transitionStyle?: ModalTransitionStyle;
} & AriaModalOverlayProps;

/** Owns backdrop, React Aria overlay state and private family nesting. */
const ModalOverlay = ({
    transitionStyle = 'fade',
    backgroundStyle = 'dim',
    children,
    ...overlayProps
}: ModalOverlayProps) => {
    const stackLayer = useModalStackLayer();
    const { familyDepth, isRoot, parent } = stackLayer;
    if (parent && !parent.isReady) return null;
    const backgroundClassName = styles.overlay.backgroundStyle[backgroundStyle];
    const handleOverlayPointerDownCapture = overlayProps.onPointerDownCapture;

    /** Blurs the active input on backdrop clicks without breaking focus containment. */
    const handlePointerDownCapture: React.PointerEventHandler<HTMLDivElement> = (event) => {
        handleOverlayPointerDownCapture?.(event);

        if (event.target !== event.currentTarget) return;

        const activeElement = document.activeElement;

        if (activeElement instanceof HTMLElement) activeElement.blur();

        const focusTarget = event.currentTarget.querySelector<HTMLElement>('[data-roma-dialog-focus]');
        focusTarget?.focus({ preventScroll: true });
    };

    /** Keeps nested portals in the current family while preserving render-prop children. */
    const renderChildren = (state: ModalRenderProps): React.ReactNode => {
        const resolvedChildren =
            typeof children === 'function'
                ? (children as (state: ModalRenderProps) => React.ReactNode)(state)
                : children;

        return (
            <ModalStackLayerProvider layer={stackLayer} state={state.state} transitionStyle={transitionStyle}>
                {resolvedChildren}
            </ModalStackLayerProvider>
        );
    };

    return (
        <AriaModalOverlay
            {...overlayProps}
            onPointerDownCapture={handlePointerDownCapture}
            className={(state) =>
                cx(
                    SEMANTIC_NAME_MODAL_OVERLAY,
                    'fixed inset-0 z-(--z-index-modal) flex w-full',
                    'overflow-y-auto overscroll-y-contain [-webkit-overflow-scrolling:touch]',
                    // The backdrop stays full height while the modal sits above any virtual keyboard,
                    // matching the modal's visual-viewport-based max height.
                    '[--modal-keyboard-inset:max(0px,100dvh-var(--visual-viewport-height))]',
                    // The modal aligns itself with auto margins (see ModalRoot)
                    'items-start justify-center px-4 pt-4 pb-[calc(clamp(16px,8vh,64px)+var(--modal-keyboard-inset))] outline-hidden',
                    'sm:justify-center sm:p-8 sm:pb-[calc(--spacing(8)+var(--modal-keyboard-inset))]',
                    isRoot ? backgroundClassName : 'bg-transparent',
                    !isRoot && 'pointer-events-auto',
                    isRoot &&
                        transitionStyle === 'fade' &&
                        state.isEntering &&
                        cx(styles.modal.transition, 'animate-in fade-in'),
                    isRoot &&
                        transitionStyle === 'fade' &&
                        state.isExiting &&
                        cx(styles.modal.transition, 'animate-out fade-out'),
                    typeof overlayProps.className === 'function'
                        ? overlayProps.className(state)
                        : overlayProps.className,
                )
            }
            style={(state) => ({
                ...(typeof overlayProps.style === 'function' ? overlayProps.style(state) : overlayProps.style),
                zIndex: `calc(var(--z-index-modal) + ${familyDepth})`,
            })}
        >
            {renderChildren}
        </AriaModalOverlay>
    );
};

ModalOverlay.displayName = SEMANTIC_NAME_MODAL_OVERLAY;

/** Renders the modal surface with entry, exit and depth scale presentation. */
const ModalRoot = (props: AriaModalOverlayProps) => {
    const presentation = useModalStackPresentation();
    const layersAbove = presentation?.layersAbove ?? 0;
    const transitionStyle = presentation?.transitionStyle ?? 'fade';
    const fades = presentation ? !presentation.isRoot : false;

    const modalScale = getModalScale(layersAbove);

    return (
        <AriaModal
            {...props}
            className={(state) =>
                cx(
                    SEMANTIC_NAME_MODAL,
                    // Auto margins bottom-align (centre from sm) but collapse when the modal is taller than the space,
                    // pinning it to the top so the overlay scrolls instead of clipping. Unlike safe alignment, this
                    // works before Safari 17.6.
                    'mt-auto sm:mb-auto',
                    'pointer-events-none max-h-[calc(var(--visual-viewport-height)-var(--spacing-7xl))] w-full max-w-[calc(100vw-var(--spacing-7xl))] align-middle outline-hidden select-none overflow-y-auto overflow-visible max-sm:rounded-xl',
                    transitionStyle === 'fade' &&
                        !state.isEntering &&
                        !state.isExiting &&
                        cx(styles.modal.transition, 'transition-transform'),
                    transitionStyle === 'fade' &&
                        state.isEntering &&
                        cx(styles.modal.transition, 'animate-in zoom-in-[1.02]', fades && 'fade-in'),
                    transitionStyle === 'fade' &&
                        state.isExiting &&
                        cx(styles.modal.transition, 'animate-out zoom-out-[1.02]', fades && 'fade-out'),
                    typeof props.className === 'function' ? props.className(state) : props.className,
                )
            }
            style={(state) => ({
                ...(typeof props.style === 'function' ? props.style(state) : props.style),
                transform: `scale(${modalScale})`,
            })}
        />
    );
};

ModalRoot.displayName = SEMANTIC_NAME_MODAL;

/** Provides the React Aria dialog wrapper. */
const Dialog = (props: AriaDialogProps) => (
    <AriaDialog
        {...props}
        className={cx(SEMANTIC_NAME_DIALOG, 'flex w-full items-center justify-center outline-hidden', props.className)}
    />
);

Dialog.displayName = SEMANTIC_NAME_DIALOG;

export type DialogContentProps = {
    children?: React.ReactNode;
    closeButton?: boolean | React.ReactNode;
    closeButtonSize?: CloseButtonProps['size'];
} & React.HTMLAttributes<HTMLDivElement>;

/** Resolves the optional dialog close control. */
const renderCloseButton = ({
    closeButton,
    closeButtonSize,
}: Pick<DialogContentProps, 'closeButton' | 'closeButtonSize'>): React.ReactNode => {
    if (!closeButton) return null;

    if (closeButton === true) return <CloseButton size={closeButtonSize} label="Close modal" slot="close" />;

    if (React.isValidElement(closeButton)) {
        return React.cloneElement(closeButton as React.ReactElement<{ slot?: string }>, { slot: 'close' });
    }

    if (isReactComponent(closeButton)) {
        const CloseButtonComponent = closeButton;
        return <CloseButtonComponent slot="close" />;
    }

    return null;
};

/** Renders the modal content surface and depth-aware filter and shadow. */
const DialogContent = ({
    children,
    className,
    closeButton,
    closeButtonSize = 'md',
    style,
    tabIndex,
    ...props
}: DialogContentProps) => {
    const presentation = useModalStackPresentation();
    const resolvedCloseButton = renderCloseButton({ closeButton, closeButtonSize });
    const contentStyle = presentation
        ? {
              ...style,
              boxShadow: getModalShadow(presentation.layersAbove),
              filter: presentation.isCovered ? 'brightness(var(--brightness-modal-covered))' : 'brightness(1)',
          }
        : (style ?? {});

    return (
        <div
            {...props}
            data-roma-dialog-focus
            data-close-button={resolvedCloseButton ? true : undefined}
            tabIndex={tabIndex ?? -1}
            style={contentStyle}
            className={cx(
                SEMANTIC_NAME_DIALOG_CONTENT,
                'pointer-events-auto relative flex w-full max-w-md rounded-2xl bg-primary py-4xl px-5xl outline-0',
                !presentation && 'shadow-xl',
                presentation?.transitionStyle === 'fade' &&
                    cx(styles.modal.transition, 'transition-[filter,box-shadow]'),
                presentation?.isCovered && 'pointer-events-none',
                className,
            )}
        >
            {children}
            {resolvedCloseButton && <div className="absolute right-3 top-3 z-10">{resolvedCloseButton}</div>}
        </div>
    );
};

DialogContent.displayName = SEMANTIC_NAME_DIALOG_CONTENT;

type ModalComponent = typeof ModalRoot & {
    Overlay: typeof ModalOverlay;
    Dialog: typeof Dialog;
    DialogContent: typeof DialogContent;
    DialogTrigger: typeof DialogTrigger;
    Header: typeof ModalHeader;
};

export const Modal = Object.assign(ModalRoot, {
    Overlay: ModalOverlay,
    Dialog,
    DialogContent,
    DialogTrigger,
    Header: ModalHeader,
}) as ModalComponent;
